/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import {fade, makeStyles} from '@material-ui/core/styles';
import {dlsColor} from '../../theme/theme';
import useThemeContext from '../../theme/useThemeContext';

/**
 * Builds the current theme's DLS floating map button states (Button/Floating)
 * shared by the map's labelled and icon-only buttons - hover, active
 * (pressed), selected and disabled, over the white default.
 *
 * ```typescript
 *
 *   import {getMapFloatingButtonStates} from
 *     '@ion/dls/src/maps/toolbar/mapFloatingButtonStyles';
 *   ...
 *
 *   const {hover, active, selected, disabled} = getMapFloatingButtonStates();
 *
 * ```
 *
 * @returns The `default`, `hover`, `active`, `selected` and `disabled` style
 *          objects, and the selected state's `accent` colour.
 */
export const getMapFloatingButtonStates = () => {
  const accent = fade(dlsColor('darkBlue'), 0.25);
  return {
    accent,
    default : {
      backgroundColor : dlsColor('white'),
      boxShadow       : '4px 4px 4px rgba(0, 0, 0, 0.25)',
      color           : dlsColor('grey1')
    },
    hover : {
      backgroundColor : dlsColor('grey6'),
      boxShadow       : '4px 4px 4px rgba(0, 0, 0, 0.3)'
    },
    active : {
      backgroundColor : dlsColor('aquamarine'),
      boxShadow       : '4px 4px 4px rgba(0, 0, 0, 0.12), inset 0px 2px 4px rgba(189, 220, 228, 0.5)'
    },
    selected : {
      backgroundColor : dlsColor('paleBlue'),
      border          : `1px solid ${accent}`,
      boxShadow       : '4px 4px 4px rgba(0, 0, 0, 0.25)',
      color           : dlsColor('darkTeal')
    },
    disabled : {
      backgroundColor : dlsColor('grey8'),
      boxShadow       : '4px 4px 4px rgba(0, 0, 0, 0.12)',
      color           : dlsColor('grey4')
    }
  };
};

/**
 * The current theme's floating map button label - the Time Mode toggle's own
 * type (the spec's "CTA 1": Inter 500, 14px/20px, untracked), and semi-bold
 * while pressed or selected, as its selected mode is.
 */
export const MAP_FLOATING_BUTTON_LABEL = {
  fontFamily    : 'Inter, Roboto, sans-serif',
  fontStyle     : 'normal',
  fontSize      : 14,
  lineHeight    : '20px',
  letterSpacing : 0,
  fontWeight    : 500
};

export const MAP_FLOATING_BUTTON_LABEL_EMPHASIS = 600;

// this hook also runs in the legacy theme (where its class goes unused) -
// the colour is a function value, so JSS reads the live theme on each render
// rather than freezing whichever theme was active when it first ran ...
const getHoverBackground = () => getMapFloatingButtonStates().hover.backgroundColor;

const useIconButtonStyles = makeStyles(() => ({
  // doubled for precedence over the floating icon button's own hover - a
  // grouped button (the zoom stepper) leaves the shadow to its group ...
  iconButton : {
    '&&:hover' : {
      backgroundColor : getHoverBackground,
      borderColor     : 'transparent',
      boxShadow       : '4px 4px 4px rgba(0, 0, 0, 0.3)'
    }
  },
  groupedIconButton : {
    '&&:hover' : {
      backgroundColor : getHoverBackground,
      borderColor     : 'transparent',
      boxShadow       : 'none'
    }
  }
}));

/**
 * Provides a hook which resolves the class giving a floating icon map button
 * (locate me, zoom to fit, zoom in / out, full screen) the current theme's
 * floating map button hover - its active, selected and disabled states
 * already match. Legacy theme buttons are left as they are.
 *
 * ```typescript
 *
 *   import {useMapFloatingIconButtonClass} from
 *     '@ion/dls/src/maps/toolbar/mapFloatingButtonStyles';
 *   ...
 *
 *   const className = useMapFloatingIconButtonClass();
 *
 *   <FloatingIconButtonGroup.FloatingIconButton className={className} />
 *
 * ```
 *
 * @param   isGrouped `true` for a button inside a grouped stepper.
 * @returns           The class name in the current theme, otherwise
 *                    `undefined`.
 */
export const useMapFloatingIconButtonClass = (isGrouped = false): string | undefined => {
  const {isCurrent} = useThemeContext();
  const classes     = useIconButtonStyles();
  return isCurrent ? (isGrouped ? classes.groupedIconButton : classes.iconButton) : undefined;
};
